"use client";

import posthog from "posthog-js";
import type { AccountType, AnalyticsEvent, AnalyticsProps } from "./events";

/**
 * Client-side PostHog wrapper (PLA2-366). All call sites import these helpers
 * rather than touching `posthog-js` directly, so the SDK choice + the no-key
 * no-op behavior live in one place.
 *
 * If NEXT_PUBLIC_POSTHOG_KEY is unset (local dev, previews without the key),
 * every call is a silent no-op — analytics must never break the app.
 */

let initialized = false;

export function initAnalytics(): void {
  if (initialized || typeof window === "undefined") return;
  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return; // no key → analytics disabled, app unaffected
  posthog.init(key, {
    api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://app.posthog.com",
    capture_pageview: false, // we capture pageviews/session_started manually
    capture_pageleave: true,
    persistence: "localStorage+cookie",
  });
  initialized = true;
}

export function isAnalyticsEnabled(): boolean {
  return initialized;
}

/**
 * Optional person-property updates to piggyback on a capture.
 * `set` overwrites; `setOnce` only writes if the property is not already set
 * (the right primitive for first-seen timestamps — it survives the
 * anonymous → identified merge that happens when a GC visitor later logs in).
 */
export interface PersonUpdate {
  set?: AnalyticsProps;
  setOnce?: AnalyticsProps;
}

export function capture(
  event: AnalyticsEvent,
  props?: AnalyticsProps,
  person?: PersonUpdate
): void {
  if (!initialized) return;
  if (person?.set || person?.setOnce) {
    posthog.capture(event, {
      ...props,
      ...(person.set ? { $set: person.set } : {}),
      ...(person.setOnce ? { $set_once: person.setOnce } : {}),
    });
    return;
  }
  posthog.capture(event, props);
}

/**
 * Attach properties to EVERY subsequent event in this session.
 *
 * Used for zone/zipcode: they are resolved once mid-funnel, but we want them on
 * downstream generation and checkout events too. Registering beats threading the
 * values through a dozen call sites.
 */
export function registerSuperProps(props: AnalyticsProps): void {
  if (!initialized) return;
  posthog.register(props);
}

/** Set person properties with no associated event. */
export function setPersonProps(person: PersonUpdate): void {
  if (!initialized) return;
  if (person.set) posthog.setPersonProperties(person.set, undefined);
  if (person.setOnce) posthog.setPersonProperties(undefined, person.setOnce);
}

/**
 * The browser's PostHog distinct id, or null when analytics is disabled.
 *
 * Threaded into the generation request payload so the backend's
 * `generation_succeeded` / `generation_failed` events (fired from
 * `api/services/analytics.py`) land on the *same* PostHog person as the client
 * events from this session. Without it the backend can only fall back to a
 * hashed email, which joins checkout but not the anonymous browsing session.
 */
export function getDistinctId(): string | null {
  if (!initialized) return null;
  try {
    return posthog.get_distinct_id() || null;
  } catch {
    return null;
  }
}

/** Manual SPA pageview (init disables auto-capture so client nav is tracked). */
export function capturePageview(url?: string): void {
  if (!initialized) return;
  posthog.capture("$pageview", url ? { $current_url: url } : undefined);
}

/** Tie subsequent events to a Pro user (call on login). */
export function identifyPro(
  userId: string,
  props: { email?: string; retailerId?: string; retailerName?: string }
): void {
  if (!initialized) return;
  posthog.identify(userId, {
    email: props.email,
    retailer_id: props.retailerId,
    retailer_name: props.retailerName,
    account_type: "pro" as AccountType,
  });
}

/** Aggregate events under a retailer (set every session, Pro + GC alike). */
export function setRetailerGroup(
  retailerId: string,
  props: { name?: string; accountType: AccountType }
): void {
  if (!initialized || !retailerId) return;
  posthog.group("retailer", retailerId, {
    name: props.name,
    account_type: props.accountType,
  });
}

export function resetAnalytics(): void {
  if (!initialized) return;
  posthog.reset();
}
